(() => {
  let scheduled = false;

  const readStockData = (productInfo) => {
    const data = productInfo.querySelector('[data-velora-variant-stock]');
    if (!data) return new Map();

    try {
      return new Map(JSON.parse(data.textContent).map((variant) => [String(variant.id), variant]));
    } catch (error) {
      console.error('Velora: inventario de variantes inválido.', error);
      return new Map();
    }
  };

  const getCartQuantity = (variantId, quantityInput) => {
    const globalQuantity = window.VeloraCartState?.getQuantity?.(variantId);
    return Number.isFinite(globalQuantity)
      ? globalQuantity
      : Number(quantityInput?.dataset.cartQuantity || 0);
  };

  const getAvailability = (variant, cartQuantity) => {
    if (!variant) {
      return { soldOut: true, maximum: 0, minimum: 1 };
    }

    const minimum = Math.max(1, Number(variant.quantity_min || 1));
    const tracksInventory =
      Boolean(variant.inventory_management) && variant.inventory_policy !== 'continue';
    const inventoryRemaining = tracksInventory
      ? Math.max(0, Number(variant.inventory_quantity || 0) - cartQuantity)
      : null;
    const ruleRemaining =
      variant.quantity_max == null
        ? null
        : Math.max(0, Number(variant.quantity_max) - cartQuantity);
    const maximum =
      inventoryRemaining == null
        ? ruleRemaining
        : ruleRemaining == null
          ? inventoryRemaining
          : Math.min(inventoryRemaining, ruleRemaining);
    const soldOut =
      variant.available === false ||
      maximum === 0 ||
      (maximum != null && maximum < minimum);

    return { soldOut, maximum, minimum };
  };

  const setButtonState = (button, disabled, label) => {
    if (!button) return;
    if (button.classList.contains('loading') && !disabled) return;
    const text = button.querySelector('span:not(.sold-out-message)') || button.querySelector('span');

    if (!button.dataset.veloraDefaultLabel && text?.textContent.trim()) {
      button.dataset.veloraDefaultLabel = text.textContent.trim();
    }

    button.toggleAttribute('disabled', disabled);
    if (disabled) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    button.dataset.veloraStockDisabled = String(disabled);

    if (text) {
      text.textContent = disabled
        ? label
        : window.variantStrings?.addToCart || button.dataset.veloraDefaultLabel || 'Agregar al carrito';
      text.classList.remove('hidden');
    }
    button.querySelector('.sold-out-message')?.classList.add('hidden');
  };

  const setPaymentState = (paymentContainer, disabled) => {
    if (!paymentContainer) return;
    paymentContainer.classList.toggle('velora-stock-disabled', disabled);
    paymentContainer.setAttribute('aria-disabled', String(disabled));

    paymentContainer.querySelectorAll('button, [role="button"]').forEach((button) => {
      if (disabled) {
        if (!button.hasAttribute('data-velora-previous-tabindex')) {
          button.setAttribute('data-velora-previous-tabindex', button.getAttribute('tabindex') || '');
          button.setAttribute('data-velora-was-disabled', String(Boolean(button.disabled)));
        }
        button.setAttribute('tabindex', '-1');
        if ('disabled' in button) button.disabled = true;
      } else {
        const previousTabindex = button.getAttribute('data-velora-previous-tabindex');
        const wasDisabled = button.getAttribute('data-velora-was-disabled') === 'true';
        if (previousTabindex === '') button.removeAttribute('tabindex');
        else if (previousTabindex != null) button.setAttribute('tabindex', previousTabindex);
        button.removeAttribute('data-velora-previous-tabindex');
        button.removeAttribute('data-velora-was-disabled');
        if ('disabled' in button) button.disabled = wasDisabled;
      }
    });
  };

  const synchronizeQuantity = (productInfo, variant, availability, cartQuantity) => {
    const quantityControl = productInfo.querySelector('.product__info-container quantity-input');
    const quantityInput = quantityControl?.querySelector('.quantity__input');
    if (!quantityControl || !quantityInput) return;

    const { soldOut, maximum, minimum } = availability;
    const minusButton = quantityControl.querySelector('.quantity__button[name="minus"]');
    const plusButton = quantityControl.querySelector('.quantity__button[name="plus"]');

    quantityInput.dataset.cartQuantity = String(cartQuantity);
    quantityControl.classList.toggle('is-velora-sold-out', soldOut);
    quantityInput.disabled = soldOut;
    quantityInput.min = soldOut ? '0' : String(minimum);
    quantityInput.dataset.min = soldOut ? '0' : String(minimum);

    if (maximum != null) {
      quantityInput.max = String(maximum);
    } else {
      quantityInput.removeAttribute('max');
    }

    let quantity = Number(quantityInput.value || 0);
    if (soldOut) quantity = 0;
    else {
      quantity = Math.max(minimum, quantity);
      if (maximum != null) quantity = Math.min(quantity, maximum);
    }
    quantityInput.value = String(quantity);

    const minusDisabled = soldOut || quantity <= minimum;
    const plusDisabled = soldOut || (maximum != null && quantity >= maximum);
    [minusButton, plusButton].forEach((button) => {
      if (!button) return;
      const disabled = button === minusButton ? minusDisabled : plusDisabled;
      button.disabled = disabled;
      button.classList.toggle('disabled', disabled);
      button.setAttribute('aria-disabled', String(disabled));
    });
  };

  const synchronizeProduct = (productInfo) => {
    const stockByVariant = readStockData(productInfo);
    if (!stockByVariant.size) return;

    const variantId = productInfo.querySelector('product-form input[name="id"]')?.value;
    const variant = stockByVariant.get(String(variantId));
    const quantityInput = productInfo.querySelector('.quantity__input');
    const cartQuantity = getCartQuantity(variantId, quantityInput);
    const availability = getAvailability(variant, cartQuantity);
    const exhaustedByCart = variant?.available !== false && availability.soldOut && cartQuantity > 0;
    const disabledLabel = exhaustedByCart ? 'Máximo en el carrito' : window.variantStrings?.soldOut || 'Agotado';
    const addButton = productInfo.querySelector('.product-form__submit');
    const paymentButton = productInfo.querySelector('.shopify-payment-button');
    const whatsappButton = productInfo.querySelector('.velora-wa-btn');

    setButtonState(addButton, availability.soldOut, disabledLabel);
    setPaymentState(paymentButton, availability.soldOut);

    if (whatsappButton) {
      whatsappButton.disabled = availability.soldOut;
      whatsappButton.setAttribute('aria-disabled', String(availability.soldOut));
      whatsappButton.title = availability.soldOut ? disabledLabel : '';
    }

    synchronizeQuantity(productInfo, variant, availability, cartQuantity);
  };

  const synchronizeCard = (productForm) => {
    const variantId = productForm.dataset.variantId;
    if (!variantId) return;

    const variant = {
      available: productForm.dataset.variantAvailable === 'true',
      inventory_quantity: Number(productForm.dataset.inventoryQuantity || 0),
      inventory_policy: productForm.dataset.inventoryPolicy,
      inventory_management: productForm.dataset.inventoryManagement || null,
      quantity_min: Number(productForm.dataset.quantityMin || 1),
      quantity_max: productForm.dataset.quantityMax
        ? Number(productForm.dataset.quantityMax)
        : null,
    };
    const cartQuantity = getCartQuantity(variantId);
    const availability = getAvailability(variant, cartQuantity);
    const label =
      variant.available && availability.soldOut && cartQuantity > 0
        ? 'Máximo en el carrito'
        : window.variantStrings?.soldOut || 'Agotado';

    setButtonState(productForm.querySelector('.quick-add__submit'), availability.soldOut, label);
  };

  const synchronizeStockControls = () => {
    scheduled = false;
    document.querySelectorAll('product-info').forEach(synchronizeProduct);
    document.querySelectorAll('product-form[data-velora-card-stock]').forEach(synchronizeCard);
  };

  const scheduleSynchronization = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(synchronizeStockControls);
  };

  const start = () => {
    synchronizeStockControls();
    document.addEventListener('change', scheduleSynchronization);
    document.addEventListener('shopify:section:load', scheduleSynchronization);
    document.addEventListener('product-info:loaded', () => {
      scheduleSynchronization();
      window.setTimeout(scheduleSynchronization, 250);
    });
    document.addEventListener('velora:product-form-settled', scheduleSynchronization);
    document.addEventListener('velora:cart-state', scheduleSynchronization);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, scheduleSynchronization);
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleSynchronization);
    }
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();
})();
